import React, { useState, useEffect } from 'react';
import { PublicHeader } from '../../components/public/PublicHeader';
import { PublicFooter } from '../../components/public/PublicFooter';
import { ProjectDetailModal } from '../../components/public/ProjectDetailModal';
import { StargazeImage } from '../../components/common/StargazeImage';
import { INITIAL_PROJECTS } from '../../data/mockData';
import { ProjectItem } from '../../types';
import { resolveProjectMedia } from '../../lib/mediaResolver';
import { getProjects } from '../../lib/cmsService';
import { 
  Film, Play, ChevronRight, Sparkles, Filter, Camera, 
  Globe, ArrowRight, Award, Clapperboard, Layers 
} from 'lucide-react';

export const FilmsPage: React.FC = () => {
  const [projects, setProjects] = useState<ProjectItem[]>(INITIAL_PROJECTS);
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        setLoading(true);
        const cmsData = await getProjects();
        const base = cmsData && cmsData.length > 0 ? cmsData : INITIAL_PROJECTS;
        const resolved = await Promise.all(base.map(resolveProjectMedia));
        if (isMounted) setProjects(resolved);
      } catch (err) {
        console.warn('Error loading films:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const publishedProjects = projects.filter((p) => p.status === 'PUBLISHED');

  const filteredProjects = publishedProjects.filter((p) => {
    if (categoryFilter === 'ALL') return true;
    const g = (p.genre || '').toUpperCase();
    if (categoryFilter === 'FEATURES') return g.includes('DRAMA') || g.includes('FEATURE') || g.includes('POLITICAL');
    if (categoryFilter === 'SHORTS') return g.includes('SHORT') || g.includes('PSYCHO');
    if (categoryFilter === 'PUBLIC') return g.includes('PUBLIC') || g.includes('AWARENESS') || g.includes('POLICE');
    if (categoryFilter === 'MUSIC') return g.includes('MUSIC') || g.includes('CONCERT');
    return true;
  });

  return (
    <div className="min-h-screen bg-white text-zinc-900 font-sans selection:bg-[#D97706] selection:text-white">
      <PublicHeader />

      {/* Hero Banner */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#F8F9FA] to-white border-b border-zinc-200 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-[#B45309] font-mono text-xs font-bold tracking-widest uppercase">
            <Clapperboard className="w-3.5 h-3.5 text-[#D97706]" />
            FLAGSHIP CINEMA SLATE & SERIES
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="space-y-3 max-w-3xl">
              <h1 className="text-4xl sm:text-6xl font-black font-serif-cinematic tracking-tight uppercase text-zinc-900 leading-none">
                STARGAZE FILMS
              </h1>
              <p className="text-zinc-600 text-base sm:text-lg font-light leading-relaxed">
                Original features, hard-hitting social narratives, award-winning shorts, and culturally defining visual productions created with world-class craftsmanship.
              </p>
            </div>

            {/* Quick Links between related services */}
            <div className="flex flex-wrap gap-2 text-xs font-mono">
              <a
                href="/rental"
                className="px-3 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border border-zinc-200 transition flex items-center gap-1.5"
              >
                <Camera className="w-3.5 h-3.5 text-[#D97706]" />
                <span>Camera Rental</span>
              </a>
              <a
                href="/distribution"
                className="px-3 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border border-zinc-200 transition flex items-center gap-1.5"
              >
                <Globe className="w-3.5 h-3.5 text-[#D97706]" />
                <span>Distribution Rights</span>
              </a>
              <a
                href="/marketing"
                className="px-3 py-2 rounded-xl bg-zinc-100 hover:bg-zinc-200 text-zinc-800 border border-zinc-200 transition flex items-center gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#D97706]" />
                <span>PR & Marketing</span>
              </a>
            </div>
          </div>

          {/* Filter Pills */}
          <div className="pt-6 flex flex-wrap items-center gap-2 border-t border-zinc-200">
            <span className="text-xs font-mono text-zinc-400 uppercase mr-2 flex items-center gap-1">
              <Filter className="w-3 h-3" /> Filter:
            </span>
            {[
              { id: 'ALL', label: 'All Titles' },
              { id: 'FEATURES', label: 'Feature Films' },
              { id: 'SHORTS', label: 'Short Cinema' },
              { id: 'PUBLIC', label: 'Public Awareness & Police' },
              { id: 'MUSIC', label: 'Music & Concerts' },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setCategoryFilter(f.id)}
                className={`px-4 py-2 rounded-xl text-xs font-mono font-bold uppercase transition ${
                  categoryFilter === f.id
                    ? 'bg-[#D97706] text-white shadow-md shadow-amber-600/20'
                    : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border border-zinc-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Grid */}
      <main className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              onClick={() => setSelectedProject(project)}
              data-cursor="PLAY"
              className="group bg-[#F8F9FA] border border-zinc-200 rounded-2xl overflow-hidden hover:border-[#D97706] hover:shadow-2xl transition duration-500 cursor-pointer flex flex-col justify-between"
            >
              <div>
                <div className="aspect-[16/9] relative overflow-hidden bg-zinc-900">
                  <StargazeImage
                    src={project.posterUrl}
                    alt={project.title}
                    focalPoint={project.focalPoint}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  
                  <span className="absolute top-3 right-3 bg-[#D97706] text-white font-mono text-[10px] font-extrabold px-2.5 py-1 rounded uppercase tracking-wider shadow-sm">
                    {project.genre.split('/')[0]}
                  </span>

                  {/* Play Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300 bg-black/40">
                    <div className="w-14 h-14 rounded-full bg-[#D97706] flex items-center justify-center text-white shadow-xl scale-90 group-hover:scale-100 transition-transform">
                      <Play className="w-6 h-6 fill-white translate-x-0.5" />
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-3">
                  <h3 className="text-xl font-bold font-serif-cinematic text-zinc-900 group-hover:text-[#D97706] transition leading-snug">
                    {project.title}
                  </h3>
                  <p className="text-xs text-zinc-600 font-sans line-clamp-3 leading-relaxed">
                    {project.synopsis}
                  </p>
                </div>
              </div>

              <div className="p-6 pt-0 space-y-3">
                <div className="pt-4 border-t border-zinc-200 flex items-center justify-between text-xs font-mono text-zinc-500">
                  <span>Director: <strong className="text-zinc-800">{project.director}</strong></span>
                  <span className="text-[#B45309] font-bold">{project.releaseYear}</span>
                </div>
                <div className="flex items-center justify-between text-[11px] font-mono text-zinc-400 group-hover:text-[#D97706] transition">
                  <span>VIEW DETAILS & CREDITS</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Co-Production & Services Banner */}
        <div className="p-8 sm:p-12 rounded-3xl bg-zinc-950 text-white relative overflow-hidden border border-zinc-800 flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl">
          <div className="space-y-3 max-w-xl relative z-10">
            <span className="text-[#D97706] font-mono text-xs font-bold uppercase tracking-widest block">
              CO-PRODUCTIONS & TALENT ALLIANCES
            </span>
            <h2 className="text-3xl font-bold font-serif-cinematic uppercase text-white">
              HAVE A FILM OR SERIES IN DEVELOPMENT?
            </h2>
            <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed font-sans">
              Stargaze Media partners with directors, writers, and international studios to co-produce, finance, shoot, post-produce, and distribute impactful stories.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 shrink-0 relative z-10 w-full md:w-auto">
            <a
              href="/contact"
              className="px-6 py-3.5 rounded-xl bg-[#D97706] hover:bg-[#B45309] text-white font-mono font-bold text-xs uppercase tracking-wider transition text-center shadow-lg shadow-amber-600/30"
            >
              PITCH A PROJECT
            </a>
            <a
              href="/capabilities"
              className="px-6 py-3.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border border-zinc-700 font-mono font-bold text-xs uppercase tracking-wider transition text-center"
            >
              STUDIO CAPABILITIES
            </a>
          </div>
        </div>
      </main>

      {/* Project Detail Modal */}
      <ProjectDetailModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />

      <PublicFooter />
    </div>
  );
};
